
import { useState } from 'react';

interface BookingFormProps {
  formData: {
    name: string;
    email: string;
    phone: string;
    message: string;
  };
  onFormChange: (data: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }) => void;
  onSubmit: () => void;
  isReady: boolean;
  isSubmitting: boolean;
  selectedDate: string | null;
  selectedTime: string | null;
}

export default function BookingForm({
  formData,
  onFormChange,
  onSubmit,
  isReady,
  isSubmitting,
  selectedDate,
  selectedTime,
}: BookingFormProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Invalid email address';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!selectedDate) newErrors.date = 'Please select a date';
    if (!selectedTime) newErrors.time = 'Please select a time slot';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit();
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 || 12;
    return `${displayH}:${minutes} ${ampm}`;
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
      <h4 className="text-sm font-semibold text-slate-900">Your Details</h4>

      <div className="space-y-4">
        <div>
          <input
            type="text"
            name="name"
            placeholder="Full Name *"
            value={formData.name}
            onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
            className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${
              errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>

        <div>
          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={(e) => onFormChange({ ...formData, email: e.target.value })}
            className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>

        <div>
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={(e) => onFormChange({ ...formData, phone: e.target.value })}
            className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all ${
              errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
            }`}
          />
          {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
        </div>

        <div>
          <textarea
            name="message"
            placeholder="Tell us about your study abroad goals (optional)"
            value={formData.message}
            onChange={(e) =>
              onFormChange({ ...formData, message: e.target.value.slice(0, 500) })
            }
            maxLength={500}
            rows={3}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none"
          />
          <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
        </div>
      </div>

      {(errors.date || errors.time) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs text-amber-700">
            <i className="ri-error-warning-line mr-1"></i>
            {errors.date || errors.time}
          </p>
        </div>
      )}

      {selectedDate && selectedTime && (
        <div className="bg-emerald-50 rounded-xl p-4 space-y-2">
          <p className="text-xs font-medium text-emerald-700">Booking Summary</p>
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <i className="ri-calendar-line text-emerald-500"></i>
            <span>
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <i className="ri-time-line text-emerald-500"></i>
            <span>{formatTime(selectedTime)}</span>
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!isReady || isSubmitting}
        className={
          `w-full py-4 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ` +
          (isReady && !isSubmitting
            ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed')
        }
      >
        {isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Processing...
          </>
        ) : (
          <>
            <i className="ri-secure-payment-line text-base"></i>
            Proceed to Payment
          </>
        )}
      </button>
    </div>
  );
}
